/* istanbul ignore file */
import React, { ReactElement, useState, useEffect, useCallback, useRef, useContext } from "react";

import {
  CardWrapper,
  PageLoading,
  SubscribeNotifyButton,
  useRequestInit,
  Label,
  hasNoRoleIsw,
  Icon
} from "adviesbox-shared";
import { RapportageState, RapportageProps } from "./infra/rapportage-schema";

import { FormikContextType } from "formik";
import { Card } from "@material-ui/core";
import { withAdviesboxFormik } from "../shared/utils/with-adviesbox-formik";
import { rapportageElementSchema } from "../shared/components/rapportage/rapportage-samenstellen-schema";
import { SaveButton, SaveButtonActions } from "../shared/components/save-button/save-button";

import { DevDebug } from "../shared/components/dev-debug/dev-debug";
import RapportageDetails from "./rapportage-details";
import { WithSaveData } from "../shared/utils/save-data";
import { NieuwRapport } from "../.generated/foundation/foundationtypes";
import { useParams } from "react-router-dom";
import { RouteParams } from "../shared/paramrouting/paramrouting-context";
import { LabelValuePair } from "adviesbox-shared/utils/types";
import { AppDataContext } from "../navigation/appdata-context";
import { sendReportRequest } from "./rapportage-api";
import { getReportDataForClipboardFromPowershell } from "./rapportage-powershell";
import { getRapportageTextResources } from "./rapportage-resources";

const Rapportage = (props: FormikContextType<RapportageState> & RapportageProps): ReactElement => {
  const { isSubmitting, values, dirty, setIsDirty } = props;
  const { elementen, filteredElementen } = values;
  const paramContext = useParams<RouteParams>();
  const { settings, user } = useRequestInit<{ vestiging: string; adviesdossier: string }>();

  const [selectedElementIndex, setSelectedElementIndex] = useState<number>();
  const isIswMedewerker = !hasNoRoleIsw(user);
  const saveButtonRef = useRef<SaveButtonActions>(null);
  const rapportGenererenWrapperRef = useRef<HTMLDivElement>(null);
  const { isPageDirty } = useContext(AppDataContext);

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty, setIsDirty]);

  const getRequestInit = useCallback(
    (correlationId: string): RequestInit => {
      const nieuwRapport: NieuwRapport = {
        adviesDossierId: paramContext.adviesdossier,
        voorstelId: props.voorstel,
        rapportStructuurId: values.structuurId,
        gewensteRapportNaam: null,
        preview: false
      };

      const formData = new FormData();
      formData.append("JsonValues", JSON.stringify(nieuwRapport));

      return {
        headers: {
          authorization: `${user?.token_type} ${user?.access_token}`,
          "Ocp-Apim-Subscription-Key": settings.OcpApimSubscriptionKey,
          vestigingId: paramContext.vestiging,
          correlationId: correlationId
        },
        method: "POST",
        body: formData
      };
    },
    [values, settings, user, paramContext, props.voorstel]
  );

  /* istanbul ignore next */
  const requestInitNieuweAanvraagHypotheekNieuweVersie = useCallback(
    (correlationId: string): RequestInit => {
      return getRequestInit(correlationId);
    },
    [getRequestInit]
  );

  const getRadioOptions = (): (LabelValuePair & { depth: number })[] => {
    return filteredElementen.map((e: { code: any; naam: any; ParentHoodstukken: any[] }) => {
      return {
        label: e.naam,
        value: e.code,
        depth: e.ParentHoodstukken.length * 2
      };
    });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const index = elementen.findIndex((x: { code: number }) => x.code === +value);
    setSelectedElementIndex(index);
  };

  const getReportDataForClipboard = (): string => {
    return getReportDataForClipboardFromPowershell(
      values,
      paramContext.adviesdossier,
      props.voorstel,
      user,
      paramContext.vestiging
    );
  };

  return (
    <div className="d-flex content_wrapper">
      <div className="content">
        {/* istanbul ignore next */ isSubmitting && <PageLoading />}
        <div className="save-btn-position">
          <div className="button-container mb-2">
            <div className="mr-2" style={{ position: "relative" }}>
              <div ref={rapportGenererenWrapperRef}>
                <SubscribeNotifyButton
                  context={props}
                  icon={null}
                  name="nieuw-rapport"
                  buttonText="Rapport genereren"
                  createCommandRequest={requestInitNieuweAanvraagHypotheekNieuweVersie}
                  url={`${settings.foundationOrigin}/FrontBus/nieuw-rapport`}
                  alwaysShow={true}
                  showSpinner={true}
                  isSubmitButton={false}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                  cursor: "pointer"
                }}
                onClick={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isPageDirty) {
                    try {
                      const result = await saveButtonRef.current?.performSave();
                      if (result === "completed") {
                        rapportGenererenWrapperRef.current?.querySelector("button")?.click();
                        return;
                      }
                    } catch {
                      return; // do not proceed on save error
                    }
                  }
                  rapportGenererenWrapperRef.current?.querySelector("button")?.click();
                }}
              />
            </div>
            {isIswMedewerker && (
              <div className="mr-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (process.env.REACT_APP_RAPPORT_CONNECTION_STRING) {
                      await sendReportRequest(
                        values,
                        paramContext.adviesdossier,
                        props.voorstel,
                        user,
                        paramContext.vestiging
                      );
                    } else {
                      await navigator.clipboard.writeText(getReportDataForClipboard());
                      alert("Je bericht is op het clipboard gekopieerd");
                    }
                  }}
                >
                  Rapport queue (dev&apos;s) <Icon name="copy" />
                </button>
              </div>
            )}
            <SaveButton context={props} forwardRef={saveButtonRef} />
          </div>
        </div>
        <CardWrapper className="master-detail-card flex-grow-1" maxRowCount={1}>
          <div className="p-1 col-xl-4 col-xl-4 col-xl-4 w-50">
            <Card>
              {filteredElementen.length === 0 && <div className="m-2">Geen aanpasbare onderdelen.</div>}

              {filteredElementen.length > 0 && (
                <div className="m-2">
                  <Label
                    caption={"Hoofdstukken"}
                    name="hoofdstukken-label"
                    tooltip={getRapportageTextResources("TooltipRapportageSelect")}
                  ></Label>
                  {getRadioOptions().map((option, index) => (
                    <label key={option.value} style={{ display: "block", marginBottom: 4 }}>
                      <span className={"mr-" + option.depth}></span>
                      <input
                        type="radio"
                        name="hoofdstukken"
                        value={option.value}
                        onChange={onChange}
                        className="mr-1"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              )}
            </Card>
          </div>
          {selectedElementIndex !== undefined && <RapportageDetails selected={selectedElementIndex} />}
        </CardWrapper>
        <DevDebug />
      </div>
    </div>
  );
};

export default withAdviesboxFormik<RapportageProps & WithSaveData<RapportageState>, RapportageState>({
  mapPropsToValues: (e: RapportageProps): RapportageState => {
    const { setIsDirty, ...state } = e;
    return state;
  },
  validationSchema: rapportageElementSchema
})(Rapportage);
